import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import api from "../authentication/axiosInstance";
import { AuthContext } from "./AuthContext";

export const AllocationSetupContext = createContext(null);

export const AllocationSetupProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading, userRole } = useContext(AuthContext);
  const [setups, setSetups] = useState([]);
  const [activeSetup, setActiveSetup] = useState(null);
  const [viewedSetupId, setViewedSetupId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [viewedSetupMissingNotice, setViewedSetupMissingNotice] = useState(null);

  const canManageSetups = userRole === "admin" || userRole === "standard";
  const canActivateSetups = userRole === "admin";

  const fetchSetups = useCallback(async () => {
    if (!isAuthenticated || !canManageSetups) {
      setSetups([]);
      setActiveSetup(null);
      return;
    }

    setLoading(true);
    try {
      const [listResponse, activeResponse] = await Promise.all([
        api.get("/allocation-setups/"),
        api.get("/allocation-setups/active/"),
      ]);
      setSetups(listResponse.data);
      setActiveSetup(activeResponse.data);
      setViewedSetupId((currentViewedId) => {
        if (currentViewedId === null || currentViewedId === undefined) {
          return activeResponse.data?.id || listResponse.data[0]?.id || null;
        }
        const stillExists = listResponse.data.some((setup) => setup.id === currentViewedId);
        if (stillExists) {
          return currentViewedId;
        }
        setViewedSetupMissingNotice(currentViewedId);
        return activeResponse.data?.id || listResponse.data[0]?.id || null;
      });
    } catch (error) {
      if (error?.response?.status === 404) {
        setSetups([]);
        setActiveSetup(null);
        setViewedSetupId(null);
      } else {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  }, [canManageSetups, isAuthenticated]);

  useEffect(() => {
    if (!authLoading) {
      fetchSetups().catch(() => {
        setSetups([]);
        setActiveSetup(null);
        setViewedSetupId(null);
      });
    }
  }, [authLoading, fetchSetups]);

  const refreshSetups = useCallback(async () => {
    await fetchSetups();
    setRefreshVersion((value) => value + 1);
  }, [fetchSetups]);

  const viewSetup = useCallback((setupId) => {
    setViewedSetupId(setupId);
    setRefreshVersion((value) => value + 1);
  }, []);

  const createSetup = useCallback(
    async ({ name, description = "", creationMode = "clone", sourceSetupId = null }) => {
      const payload = {
        name,
        description,
        creation_mode: creationMode,
      };
      if (sourceSetupId !== null && sourceSetupId !== undefined) {
        payload.source_setup_id = sourceSetupId;
      }
      const response = await api.post("/allocation-setups/", payload);
      await refreshSetups();
      viewSetup(response.data.id);
      return response.data;
    },
    [refreshSetups, viewSetup]
  );

  const duplicateSetup = useCallback(
    async (setupId, { name, description = "" }) => {
      const response = await api.post(`/allocation-setups/${setupId}/duplicate/`, {
        name,
        description,
      });
      await refreshSetups();
      viewSetup(response.data.id);
      return response.data;
    },
    [refreshSetups, viewSetup]
  );

  const updateSetup = useCallback(
    async (setupId, payload) => {
      const response = await api.patch(`/allocation-setups/${setupId}/`, payload);
      await refreshSetups();
      return response.data;
    },
    [refreshSetups]
  );

  const deleteSetup = useCallback(
    async (setupId) => {
      const response = await api.delete(`/allocation-setups/${setupId}/`);
      await refreshSetups();
      return response.data;
    },
    [refreshSetups]
  );

  const activateSetup = useCallback(
    async (setupId) => {
      const response = await api.post(`/allocation-setups/${setupId}/activate/`);
      await refreshSetups();
      viewSetup(response.data.id);
      return response.data;
    },
    [refreshSetups, viewSetup]
  );

  const lockSetup = useCallback(
    async (setupId) => {
      const response = await api.post(`/allocation-setups/${setupId}/lock/`);
      await refreshSetups();
      return response.data;
    },
    [refreshSetups]
  );

  const getReopenPreview = useCallback(async (setupId) => {
    const response = await api.get(`/allocation-setups/${setupId}/reopen-preview/`);
    return response.data;
  }, []);

  const reopenSetup = useCallback(
    async (setupId, payload = {}) => {
      const response = await api.post(`/allocation-setups/${setupId}/reopen/`, payload);
      await refreshSetups();
      return response.data;
    },
    [refreshSetups]
  );

  const viewedSetup = useMemo(
    () => {
      if (viewedSetupId !== null && viewedSetupId !== undefined) {
        return setups.find((setup) => setup.id === viewedSetupId) || null;
      }
      return activeSetup || setups[0] || null;
    },
    [activeSetup, setups, viewedSetupId]
  );

  const selectViewedSetup = useCallback((setupId) => {
    setViewedSetupMissingNotice(null);
    viewSetup(setupId);
  }, [viewSetup]);

  const clearViewedSetupMissingNotice = useCallback(() => {
    setViewedSetupMissingNotice(null);
  }, []);

  const getSetupApiPath = useCallback(
    (suffix) => {
      const viewedSetupExists =
        viewedSetupId !== null &&
        viewedSetupId !== undefined &&
        setups.some((setup) => setup.id === viewedSetupId);
      const setupId = viewedSetupExists ? viewedSetupId : !viewedSetupId ? activeSetup?.id : null;
      const fallbackSetupId = setupId || activeSetup?.id || setups[0]?.id;
      if (!fallbackSetupId) {
        throw new Error("No allocation setup selected.");
      }
      return `/allocation-setups/${fallbackSetupId}/${suffix.replace(/^\//, "")}`;
    },
    [activeSetup?.id, setups, viewedSetupId]
  );

  const value = useMemo(
    () => ({
      setups,
      activeSetup,
      viewedSetup,
      loading,
      refreshVersion,
      viewedSetupMissingNotice,
      canManageSetups,
      canActivateSetups,
      clearViewedSetupMissingNotice,
      refreshSetups,
      selectViewedSetup,
      getSetupApiPath,
      createSetup,
      duplicateSetup,
      updateSetup,
      deleteSetup,
      activateSetup,
      lockSetup,
      getReopenPreview,
      reopenSetup,
    }),
    [
      setups,
      activeSetup,
      viewedSetup,
      loading,
      refreshVersion,
      viewedSetupMissingNotice,
      canManageSetups,
      canActivateSetups,
      clearViewedSetupMissingNotice,
      refreshSetups,
      selectViewedSetup,
      getSetupApiPath,
      createSetup,
      duplicateSetup,
      updateSetup,
      deleteSetup,
      activateSetup,
      lockSetup,
      getReopenPreview,
      reopenSetup,
    ]
  );

  return <AllocationSetupContext.Provider value={value}>{children}</AllocationSetupContext.Provider>;
};

